import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Frame, RotateCcw, Share2, ArrowRight, Images, Download } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import WinnerTicker from "@/components/WinnerTicker";
import { fireCelebration } from "@/lib/confetti";
import { useSaveImage, isMobile } from "@/components/SaveImage";
import { usePageMeta } from "@/lib/usePageMeta";

const GRAPHIC = "/bgjc-winner-graphic.png";

export default function Congrats({ gate = false }) {
  usePageMeta({
    title: "Congratulations, Winner! | #BlackGirlJoyChallenge",
    description: "You're a 2026 #BlackGirlJoyChallenge grant recipient. Download your official post, create your custom frame and winner page, then share your joy.",
  });
  const navigate = useNavigate();
  const { search } = useLocation();
  const [ready, setReady] = useState(!gate);
  const [burst, setBurst] = useState(0);
  const played = useRef(false);

  useEffect(() => {
    if (!gate) return;
    if (new URLSearchParams(search).get("edit") === "1") navigate(`/bgjcpt/create${search}`, { replace: true });
    else setReady(true);
  }, [gate, search, navigate]);

  useEffect(() => {
    if (!ready || played.current) return;
    played.current = true;
    const t = setTimeout(() => { fireCelebration(); setBurst((b) => b + 1); }, 300);
    return () => clearTimeout(t);
  }, [ready]);

  const replay = () => { fireCelebration(); setBurst((b) => b + 1); };
  const { save: saveImg, sheet: saveSheet } = useSaveImage();

  if (!ready) return null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {saveSheet}
      <SiteNav />
      <section className="relative overflow-hidden bg-sbg-burgundy">
        <div className="absolute inset-0 joy-dotgrid opacity-20" />
        <div className="celebrate-glow absolute inset-0" key={burst} />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 py-12 lg:grid-cols-2 lg:py-16">
          <div className="order-1 flex flex-col items-center">
            <div className="celebrate-pop relative" key={`g-${burst}`}>
              <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-sbg-fuchsia/30 blur-3xl" />
              <img
                src={GRAPHIC}
                alt="I'm a #BlackGirlJoyChallenge Grant Recipient — Southern Black Girls x Pete & Thomas Foundation"
                className="w-full max-w-sm rounded-3xl shadow-2xl ring-4 ring-white/10"
                data-testid="congrats-graphic"
              />
            </div>
            <button
              type="button"
              onClick={() => saveImg(GRAPHIC, "BlackGirlJoyChallenge-Grant-Recipient.png", "I'm a #BlackGirlJoyChallenge Grant Recipient!")}
              data-testid="congrats-download-btn"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-sbg-burgundy shadow-lg transition-transform hover:-translate-y-1"
            >
              <Download className="h-4 w-4 text-sbg-fuchsia" /> {isMobile() ? "Save your official recipient post to Photos" : "Download your official recipient post"}
            </button>
            <p className="mt-2 max-w-xs text-center text-xs text-sbg-blush/80">
              Post and tag <span className="font-bold text-white">@southernblackgirls</span> &amp; <span className="font-bold text-white">@petethomasfdn</span>
            </p>
          </div>
          <div className="animate-fade-up order-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white">
              <Sparkles className="h-3.5 w-3.5" /> 2026 Grant Recipient
            </span>
            <h1 className="celebrate-title mt-5 font-display text-4xl font-black leading-tight text-white text-balance sm:text-5xl lg:text-6xl" data-testid="congrats-heading">
              Congratulations, Joy Maker!
            </h1>
            <p className="mt-4 max-w-lg text-base text-sbg-blush md:text-lg">
              You're one of 100 Black girls across the South chosen to spread joy in your community. The world should know — let's make it official.
            </p>
            <ol className="mt-6 space-y-3 text-sm text-sbg-blush/95">
              <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 font-bold text-white">1</span><span><span className="font-bold text-white">Download</span> the official recipient post and share it.</span></li>
              <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 font-bold text-white">2</span><span><span className="font-bold text-white">Create</span> your custom frame and winner landing page.</span></li>
              <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/15 font-bold text-white">3</span><span><span className="font-bold text-white">Spread the joy</span> with #BlackGirlJoyChallenge.</span></li>
            </ol>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/bgjcpt/create"
                data-testid="congrats-create-btn"
                className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-7 py-3.5 font-bold text-white transition-transform hover:-translate-y-1"
              >
                <Frame className="h-5 w-5" /> Create your custom frame &amp; winner page <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={replay}
                data-testid="congrats-replay-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3.5 font-bold text-white transition-colors hover:bg-white/20"
              >
                <RotateCcw className="h-4 w-4" /> Replay celebration
              </button>
            </div>
            <p className="mt-6 flex max-w-lg items-start gap-2 text-sm text-sbg-blush/90">
              <Share2 className="mt-0.5 h-4 w-4 shrink-0 text-sbg-turquoise" />
              <span>Tag <span className="font-bold text-white">@southernblackgirls</span> and <span className="font-bold text-white">@petethomasfdn</span> with <span className="font-bold text-white">#BlackGirlJoyChallenge</span> so we can celebrate you too.</span>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-5 py-12">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-sbg-raspberry">Meet your fellow winners</h2>
            <p className="mt-1 text-base text-sbg-burgundy/70 md:text-lg">Tap a frame to visit their winner page.</p>
          </div>
          <Link to="/bgjcpt/gallery" data-testid="congrats-gallery-link" className="inline-flex items-center gap-1.5 text-sm font-bold text-sbg-fuchsia hover:underline">
            <Images className="h-4 w-4" /> See the full gallery
          </Link>
        </div>
        <WinnerTicker />
      </section>
      <SiteFooter />
    </div>
  );
}
